import { Outlet, NavLink, Link } from "react-router-dom";
import { MessageCircle, MapPin, Mail, Phone, Instagram, Menu, X, UserCircle } from "lucide-react";
import { useState } from "react";
import { CLINIC, WHATSAPP_URL } from "@/data/clinic";
import { fireWhatsApp } from "@/lib/conversions";
import CookieBanner from "@/components/CookieBanner";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";

export default function ClinicLayout() {
  const [open, setOpen] = useState(false);
  const { t } = useI18n();
  const { user } = useAuth();
  const nav = [
    { to: "/", label: t("nav.home"), end: true },
    { to: "/tratamientos", label: t("nav.treatments") },
    { to: "/sobre-nosotros", label: t("nav.about") },
    { to: "/blog", label: t("nav.blog") },
    { to: "/faq", label: t("nav.faq") },
    { to: "/contacto", label: t("nav.contact") },
  ];
  return (
    <div data-surface="clinic" className="min-h-screen bg-[hsl(var(--clinic-bg))] text-[hsl(var(--clinic-text))]">
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--clinic-border))] bg-[hsl(var(--clinic-bg))]/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 lg:px-10">
          <Link to="/" data-testid="clinic-logo-link" className="flex items-center gap-3">
            <img src="/logo-katia.png" alt="Katia Falón Odontología" className="h-12 w-12 shrink-0 object-contain" />
            <div className="flex flex-col items-center leading-none text-center">
              <span className="font-brand block text-[34px] leading-[0.9] text-[hsl(var(--clinic-secondary))]" style={{whiteSpace:"nowrap"}}>Katia Falón</span>
              <span className="mt-1.5 block text-[10px] font-bold uppercase tracking-[0.32em] text-[hsl(var(--clinic-muted))]">Odontología</span>
            </div>
          </Link>
          <nav className="hidden items-center gap-8 lg:flex">
            {nav.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.end}
                data-testid={`nav-${n.label.toLowerCase().replace(/ /g,"-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium transition-colors hover:text-[hsl(var(--clinic-secondary))] ${
                    isActive ? "text-[hsl(var(--clinic-secondary))]" : "text-[hsl(var(--clinic-text))]"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <LanguageSwitcher />
            {user?.role === "patient" ? (
              <Link to="/paciente/panel" data-testid="header-patient-panel" className="inline-flex items-center gap-1 text-sm font-semibold text-[hsl(var(--clinic-secondary))]">
                <UserCircle size={16} /> {t("nav.myPanel")}
              </Link>
            ) : (
              <Link to="/paciente/login" data-testid="header-patient-login" className="text-sm font-medium text-[hsl(var(--clinic-muted))] hover:text-[hsl(var(--clinic-text))]">
                {t("nav.myAppt")}
              </Link>
            )}
            <Link
              to="/reservar"
              data-testid="header-book-cta"
              className="inline-flex items-center rounded-full bg-[hsl(var(--clinic-secondary))] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
            >
              {t("nav.book")}
            </Link>
          </div>
          <button
            className="lg:hidden"
            data-testid="mobile-menu-toggle"
            aria-label="Menú"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        {open && (
          <div className="border-t border-[hsl(var(--clinic-border))] bg-[hsl(var(--clinic-bg))] lg:hidden">
            <div className="mx-auto max-w-7xl px-6 py-4">
              <div className="flex flex-col gap-3">
                {nav.map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.end}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/ /g,"-")}`}
                    className="py-1 text-base font-medium"
                  >
                    {n.label}
                  </NavLink>
                ))}
                <Link
                  to="/reservar"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-book-cta"
                  className="mt-2 inline-flex w-fit items-center rounded-full bg-[hsl(var(--clinic-secondary))] px-5 py-2.5 text-sm font-semibold text-white"
                >
                  {t("nav.book")}
                </Link>
                <div className="mt-3"><LanguageSwitcher /></div>
              </div>
            </div>
          </div>
        )}
      </header>

      <main>
        <Outlet />
      </main>

      <footer className="mt-24 border-t border-[hsl(var(--clinic-border))] bg-[hsl(var(--clinic-surface))]">
        <div className="mx-auto grid max-w-7xl gap-10 px-6 py-16 lg:grid-cols-4 lg:px-10">
          <div>
            <div className="flex items-center gap-3">
              <img src="/logo-katia.png" alt="" className="h-12 w-12 shrink-0 object-contain" />
              <div className="flex flex-col items-center leading-none text-center">
                <div className="font-brand block text-[34px] leading-[0.9] text-[hsl(var(--clinic-secondary))]" style={{whiteSpace:"nowrap"}}>Katia Falón</div>
                <div className="mt-1.5 block text-[10px] font-bold uppercase tracking-[0.32em] text-[hsl(var(--clinic-muted))]">Odontología</div>
              </div>
            </div>
            <p className="mt-4 max-w-xs text-sm text-[hsl(var(--clinic-muted))]">
              {t("footer.tagline")}
            </p>
            <div className="mt-4 flex items-center gap-2">
              <img src="/logo-katia.png" alt="" className="hidden" />
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">
              {t("footer.contact")}
            </h4>
            <ul className="mt-4 space-y-3 text-sm">
              <li className="flex items-start gap-2"><MapPin size={16} className="mt-0.5 shrink-0" />{CLINIC.address}</li>
              <li className="flex items-start gap-2">
                <Phone size={16} className="mt-0.5 shrink-0" />
                <a data-testid="footer-phone" href={`tel:${CLINIC.phone}`}>{CLINIC.phoneDisplay}</a>
              </li>
              <li className="flex items-start gap-2">
                <Mail size={16} className="mt-0.5 shrink-0" />
                <a data-testid="footer-email" href={`mailto:${CLINIC.email}`}>{CLINIC.email}</a>
              </li>
              <li className="flex items-start gap-2">
                <Instagram size={16} className="mt-0.5 shrink-0" />
                <a data-testid="footer-instagram" href={`https://instagram.com/${CLINIC.instagram.replace("@","")}`} target="_blank" rel="noreferrer">
                  {CLINIC.instagram}
                </a>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">
              {t("footer.hours")}
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              {CLINIC.hours.map((h) => (
                <li key={h.day} className="flex justify-between gap-4">
                  <span>{h.day}</span>
                  <span className="text-[hsl(var(--clinic-muted))]">{h.hours}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h4 className="text-sm font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">
              {t("footer.legal")}
            </h4>
            <ul className="mt-4 space-y-2 text-sm">
              <li><Link to="/legal" data-testid="footer-legal-link">{t("footer.legalPrivacy")}</Link></li>
              <li><Link to="/faq">{t("footer.faq")}</Link></li>
              <li><Link to="/pqrs" data-testid="footer-pqrs-link">{t("footer.pqrs")}</Link></li>
              <li><Link to="/mi-cita" data-testid="footer-my-appointment">{t("footer.myAppointment")}</Link></li>
              <li><Link to="/blog">{t("footer.blog")}</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[hsl(var(--clinic-border))] py-6 text-center text-xs text-[hsl(var(--clinic-muted))]">
          © {new Date().getFullYear()} Katia Falón Odontología · Armenia, Quindío · {t("footer.copyright")}
        </div>
      </footer>

      <a
        href={WHATSAPP_URL}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => fireWhatsApp(WHATSAPP_URL, "float")}
        data-testid="whatsapp-float"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-full bg-[#25D366] px-4 py-3 text-sm font-semibold text-white shadow-lg transition-transform hover:-translate-y-0.5"
        aria-label="Escríbenos por WhatsApp"
      >
        <MessageCircle size={20} />
        <span className="hidden sm:inline">WhatsApp</span>
      </a>
      <CookieBanner />
    </div>
  );
}
